import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useQuery } from '@apollo/client/react'

import { DisplayDate } from 'js/components/DisplayDate'
import FetchMore from 'js/components/FetchMore'
import ItemActions from 'js/components/Item/ItemActions/ItemActions'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import Spacer from 'js/components/Spacer/Spacer'
import Table from 'js/components/Table/Table'
import Text from 'js/components/Text/Text'
import getUrls from 'js/magazine/lib/getUrls'
import refetchQueries from 'js/magazine/lib/refetchQueries'
import { MAGAZINES_LIST } from 'js/magazine/List/components/MagazineList.queries'

import useTableHeaders from './useTableHeaders'

const MagazineList = () => {
    const { t } = useTranslation()
    const tableHeaders = useTableHeaders()

    const PAGE_SIZE = 50

    const [orderBy, setOrderBy] = useState('title')
    const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc')

    const { loading, data, fetchMore, refetch } = useQuery<any>(
        MAGAZINES_LIST,
        {
            variables: {
                parentGuid: '1',
                offset: 0,
                limit: PAGE_SIZE,
                orderBy,
                orderDirection,
            },
            fetchPolicy: 'cache-and-network',
        },
    )

    useEffect(() => {
        refetch()
    }, [orderBy, orderDirection, refetch])

    return (
        <Spacer>
            {loading ? (
                <LoadingSpinner />
            ) : data.entities.edges.length === 0 ? (
                <Text textAlign="center">{t('admin.pages-not-found')}</Text>
            ) : (
                <Table
                    rowHeight="normal"
                    headers={tableHeaders}
                    orderBy={orderBy}
                    orderDirection={orderDirection}
                    setOrderBy={setOrderBy}
                    setOrderDirection={setOrderDirection}
                >
                    <FetchMore
                        as="tbody"
                        edges={data.entities.edges}
                        getMoreResults={(data) => data.entities.edges}
                        fetchMore={fetchMore}
                        fetchCount={25}
                        maxLimit={data.entities.total}
                        resultsMessage={t('global.result', {
                            count: data.entities.total,
                        })}
                        fetchMoreButtonHeight={48}
                    >
                        {data.entities.edges.map((entity) => {
                            const {
                                guid,
                                url,
                                title,
                                owner,
                                timePublished,
                                timeUpdated,
                            } = entity

                            const selectedDateSorting =
                                orderBy === 'timePublished'
                                    ? timePublished
                                    : timeUpdated

                            const urls = getUrls(guid)

                            return (
                                <tr key={guid}>
                                    <td>
                                        <Link to={url} className="TableLink">
                                            {title}
                                        </Link>
                                    </td>
                                    <td>{owner.name}</td>
                                    <td
                                        style={{
                                            textAlign: 'right',
                                        }}
                                    >
                                        <DisplayDate
                                            date={selectedDateSorting}
                                            type="date"
                                        />
                                    </td>
                                    <td>
                                        <ItemActions
                                            entity={entity}
                                            onEdit={urls.edit}
                                            onAfterDelete={urls.list}
                                            hideArchive
                                            refetchQueries={
                                                refetchQueries as any
                                            }
                                        />
                                    </td>
                                </tr>
                            )
                        })}
                    </FetchMore>
                </Table>
            )}
        </Spacer>
    )
}

export { MAGAZINES_LIST }
export default MagazineList
